import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  highlight?: number;
  label?: string;
}
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-6069">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-6070">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-6071">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-6072">
            <span className="text-2xl" data-id="element-6073">🔤</span>
            <div className="text-left" data-id="element-6074">
              <div className="text-sm font-bold text-green-900" data-id="element-6075">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-6076">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-6077">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-6078">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-6079">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-6080">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-6081">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-6082">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = highlight === index;
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-6083">

                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-6084">

                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-6085">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                            `} data-id="element-6086">

                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-6087">
                      <span className="text-xs font-bold" data-id="element-6088">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-6089">

                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-6090">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-6091">
            <div className="flex items-start gap-3" data-id="element-6092">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-6093">
                <span className="text-blue-600 text-lg" data-id="element-6094">👀</span>
              </div>
              <div data-id="element-6095">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-6096">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-6097">
                  <p data-id="element-6098">
                    Het woord <strong data-id="element-6099">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-6100">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-6101">
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-6102">

                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-6103">
                    <p className="flex items-center gap-2" data-id="element-6104">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-6105"></span>
                      <span data-id="element-6106">
                        <strong data-id="element-6107">Groen (○):</strong> Open lettergreep = lange
                        klank (één medeklinker na de klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-6108">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-6109"></span>
                      <span data-id="element-6110">
                        <strong data-id="element-6111">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (twee medeklinkers na de klinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-6112">
            <div className="flex items-start gap-3" data-id="element-6113">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-6114">
                <span className="text-emerald-600 text-lg" data-id="element-6115">📖</span>
              </div>
              <div data-id="element-6116">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-6117">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-6118">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-6119">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-6120">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-6121">
                      Eindigt op een <strong data-id="element-6122">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-6123">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-6124">
                      Voorbeeld: <strong data-id="element-6125">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-6126">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-6127">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-6128">
                      Eindigt op een <strong data-id="element-6129">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-6130">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-6131">
                      Voorbeeld: <strong data-id="element-6132">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-6133">
            <div className="flex items-start gap-3" data-id="element-6134">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-6135">
                <span className="text-indigo-600 text-lg" data-id="element-6136">✍️</span>
              </div>
              <div data-id="element-6137">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-6138">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-6139">
                  <p data-id="element-6140">
                    <strong data-id="element-6141">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-6142">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-6143">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-6144">
                    <strong data-id="element-6145">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-6146">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-6147">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-6148">
            <div className="flex items-start gap-3" data-id="element-6149">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-6150">
                <span className="text-amber-600 text-lg" data-id="element-6151">💡</span>
              </div>
              <div data-id="element-6152">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-6153">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-6154">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-6155">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-6156">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-6157">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-6158">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-6159">open</strong> en schrijf je{' '}
                  <strong data-id="element-6160">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}